/**
 * CLONE REBRAND FILE — keep in sync with src/assets/css/theme.css
 *
 * Checklist for a new project clone:
 * 1. Set APP.* (name, title, copyright year, developerCredit)
 * 2. Set BRAND.* (primary, accents, danger)
 * 3. Set BUTTONS.* / matching --btn-* in theme.css (Add/Excel/PDF/Save/Cancel/Close)
 * 4. Set TYPOGRAPHY.* / --font-* (family + sizes)
 * 5. Swap logo assets (Sidebar / Login) + account icon silhouette (user_icon.png)
 * 6. Adjust GRADIENTS / TOP_10_RANK_COLORS if needed
 * 7. Smoke: Login, list Add, form Save/Close, Report Excel/PDF, Dashboard cards, navbar account icon
 */

/** App identity — change once per clone (title, footer, login copy, labels) */
export const APP = {
  name: "BIZHUB",
  shortName: "BIZHUB",
  /** Browser tab title */
  documentTitle: "BIZHUB - Backend",
  /** Footer copyright year */
  copyrightYear: 2026,
  get copyright() {
    return `Copyright © ${this.copyrightYear}. ${this.name}. All Rights Reserved.`;
  },
  /** Login hero supporting line — use {name} placeholder */
  loginTagline:
    "The all-in-one platform for {name} — manage members, track referrals, and grow your network effortlessly.",
  logoAlt: "{name} Logo",
  memberLabel: "{name} Member",
  /**
   * Footer + Login “Design & Developed By” credit.
   * Toggle `show` to enable/disable in both places.
   */
  developerCredit: {
    show: true,
    url: "https://www.innoblitz.global/",
    label: "Design & Developed By",
    company: "Innoblitz Technologies And Systems Pvt Ltd",
    color: "#0033A0",
    fontSize: "15px",
  },
  /** Navbar center live clock — toggle `show` to hide/show */
  navbarClock: {
    show: true,
  },
};

export const appText = (template) =>
  String(template).replace(/\{name\}/g, APP.name);

export const BRAND = {
  red: "#cd1b19",
  redRgb: [227, 6, 19],
  navy: "#0033A0",
  navyRgb: [0, 51, 160],
  navyLight: "#1a4bb8",
  navyDark: "#00267a",
  navyMid: "#0A3C9C",
  /** Action buttons: Add, Save, Excel, PDF, Sign in */
  primary: "#cd1b19",
  primaryRgb: [227, 6, 19],
  primaryArgb: "FFE30613",
  primaryLight: "#FF3A45",
  primaryDark: "#B8050F",
  primaryMid: "#C80610",
  accentYellow: "#ffd54f",
  danger: "#B8050F",
  dangerHover: "#8C040B",
  white: "#FFFFFF",
  whiteArgb: "FFFFFFFF",
  textOnPrimary: "#FFFFFF",
  mutedBg: "#efefef",
  mutedBorder: "#F6D5D7",
  mutedText: "#3d4152",
  goBackBg: "#FDECEE",
  /** Navbar / default account avatar circle — mirrors --brand-account-icon-bg */
  get accountIconBg() {
    return this.primary;
  },
  /** Modal top-right X button — mirrors --brand-modal-dismiss-bg */
  get modalDismissBg() {
    return this.primary;
  },
  /** Dashboard page title icon — sidebar navy */
  get dashboardTitleIcon() {
    return this.navy;
  },
};

export const TYPOGRAPHY = {
  fontFamily: "'Inter', sans-serif",
  xs: "12px",
  sm: "13px",
  md: "14px",
  base: "15px",
  lg: "16px",
  xl: "17px",
  "2xl": "20px",
  "3xl": "22px",
  button: "13px",
  buttonWeight: 400,
  buttonWeightBold: 700,
  pageTitle: "24px",
  tableHeader: "15px",
};

/** Semantic button configs — clone projects tweak here; CSS vars drive visuals */
export const BUTTONS = {
  add: {
    bg: BRAND.primary,
    color: BRAND.white,
    hoverBg: BRAND.primaryDark,
    border: "none",
    radius: "8px",
    fontSize: TYPOGRAPHY.button,
    fontWeight: TYPOGRAPHY.buttonWeight,
    padding: "6px 12px",
    minWidth: "64px",
    className: "btn rounded-pill btn-primary ibutton",
  },
  excel: {
    bg: BRAND.primary,
    color: BRAND.white,
    hoverBg: BRAND.primaryDark,
    className: "btn rounded-pill btn-primary excel-btn ibutton",
    fontSize: TYPOGRAPHY.button,
  },
  pdf: {
    bg: BRAND.primary,
    color: BRAND.white,
    hoverBg: BRAND.primaryDark,
    className: "btn rounded-pill btn-primary pdf-btn ibutton",
    fontSize: TYPOGRAPHY.button,
  },
  save: {
    bg: BRAND.primary,
    color: BRAND.white,
    border: BRAND.primary,
    className: "btn btn-success isubmit_button",
    fontSize: TYPOGRAPHY.button,
    fontWeight: TYPOGRAPHY.buttonWeightBold,
  },
  cancel: {
    bg: "#ccc",
    color: "#000",
    border: "#ccc",
    className: "btn btn-primary iclose_btn",
    fontSize: TYPOGRAPHY.button,
  },
  close: {
    bg: "#ccc",
    color: "#000",
    border: "#ccc",
    className: "btn btn-secondary ibutton",
    fontSize: TYPOGRAPHY.button,
  },
};

export const TOP_10_RANK_COLORS = [
  "#c4161c",
  "#c4161c",
  "#c4161c",
  "#c4161c",
  "#c4161c",
  "#c4161c",
  "#c4161c",
  "#c4161c",
  "#c4161c",
  "#c4161c",
];

export const getTop10RankColor = (index) =>
  TOP_10_RANK_COLORS[Math.min(index, 9)];

// export const getTop10RankTextColor = (index) =>
//   index >= 4 ? BRAND.primary : BRAND.white;

export const getTop10RankTextColor = () => BRAND.white;

export const GRADIENTS = {
  birthdayCard: "linear-gradient(135deg, #c4161c 0%, #e52a31 100%)",
  meetingAbsentees: "linear-gradient(135deg, #c4161c 0%, #e52a31 100%)",
  pendingMeetingFees: "linear-gradient(135deg, #c4161c 0%, #e52a31 100%)",
  topAchieversHeader: "linear-gradient(90deg, #c4161c 0%, #b0141a 55%, #9a1116 100%)",
  analyticsCard: "linear-gradient(135deg, #c4161c 0%, #e52a31 100%)",
  /** Stat cards grid background (Dashboard.css .analytics-grid-card) */
  analyticsGridCard: "linear-gradient(to right, #c4161c 0%, #e52a31 100%)",
  loginButton: `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.primaryLight} 100%)`,
};

export const SHADOWS = {
  insightCard: `0 6px 16px rgba(${BRAND.navyRgb.join(", ")}, 0.25)`,
};

export const rgbaPrimary = (alpha) =>
  `rgba(${BRAND.primaryRgb.join(", ")}, ${alpha})`;

export const rgbaNavy = (alpha) =>
  `rgba(${BRAND.navyRgb.join(", ")}, ${alpha})`;

export const CHART_PALETTE = [
  "#1E3A93",
  "#D6201F",
  "#6B5BD2",
  "#0E9AA7",
  "#F08A24",
  "#5B6679",
];

export const CHART = {
  areaFill: "rgba(30, 58, 147, 0.12)",
  softIconBg: "#E8EDFB",
};

export const PDF_HEADER = {
  fillColor: BRAND.navyRgb,
  textColor: [255, 255, 255],
};

export const pdfHeaderStyles = {
  fillColor: PDF_HEADER.fillColor,
  textColor: PDF_HEADER.textColor,
};

export const LOADER_COLORS = [`${BRAND.navy}e8`, BRAND.navy];

/** Chapter create/edit color picker defaults */
export const CHAPTER = {
  defaultBackgroundColor: BRAND.navy,
  defaultColor: BRAND.white,
};

/** Shared react-select styles for filter panels — focused/selected use brand + white text */
export const selectFilterStyles = {
  control: (base) => ({ ...base, fontSize: "0.85rem", minHeight: "36px" }),
  placeholder: (base) => ({
    ...base,
    fontSize: "0.85rem",
    color: "#222",
    fontWeight: "500",
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: "0.85rem",
    color: "#222",
    fontWeight: "500",
  }),
  option: (base, state) => ({
    ...base,
    fontSize: "0.85rem",
    backgroundColor: state.isSelected
      ? BRAND.navy
      : state.isFocused
        ? BRAND.navyLight
        : undefined,
    color:
      state.isSelected || state.isFocused ? BRAND.textOnPrimary : "#222",
    cursor: "pointer",
  }),
  input: (base) => ({ ...base, fontSize: "0.85rem", color: "#222" }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
};
